/* eslint-disable max-lines */
import React, { useState, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getTimezones } from "api";
import moment from "moment";
import { useGetCountries } from "api/queries/common/useGetCountries";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { ICountryOption } from "types/Common";
import { IPickDeliveryProps, ITimezones } from "./Types";
import PickupDeliveryAddress from "./PickupDeliveryAddress/PickupDeliveryAddress";
import PickupDeliveryContact from "./PickupDeliveryContact";
import PickupDeliveryPlanning from "./PickupDeliveryPlanning";
import "./Schedule.scss";

function PickupDelivery(props: IPickDeliveryProps) {
  const {
    isPickupOrDelivery,
    isQuoteRequest,
    contactBook,
    scheduleOptions,
    isOffPlatformShipment,
    isNewShipment,
    isMiniTender = false,
    formState,
  } = props || {};

  const methods = useFormContext();
  const { setValue, getValues, watch, control } = methods;
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);

  const [timezones, setTimezones] = useState<ITimezones[]>([]);

  const isShipment = location.pathname === "/tracker";
  const { service_levels: isServiceLevelEnabled } = scheduleOptions as any;

  const state = watch(`schedule.${isPickupOrDelivery}_state`);
  const countryWatch = useWatch({ name: `schedule.${isPickupOrDelivery}_country`, control });
  const addressTypeWatch = useWatch({ name: `schedule.${isPickupOrDelivery}_address_type`, control });
  const pickupDate = useWatch({ name: "schedule.pickup_ready_date", control });
  const serviceLevel = useWatch({ name: "service.service_level", control });

  const {
    data: countries = [],
    isLoading,
    error,
  } = useGetCountries({
    select: (data) => data.filter((country: { label: string }) => country.label !== "USA"),
    refetchOnMount: false,
  });

  useEffect(() => {
    if (isLoading || error) return;

    // Check if a country code exists in form values, then update the country field
    const countryCode = getValues(`schedule.${isPickupOrDelivery}_country_code`);
    if (countryCode) {
      const selectedCountry = countries.find((c: ICountryOption) => c.value === countryCode);
      if (selectedCountry?.label) {
        setValue(`schedule.${isPickupOrDelivery}_country`, selectedCountry.label);
      }
    }
  }, [countries, isLoading, error]);

  useEffect(() => {
    const isAirportOrSeaport =
      isOptionalPortAddressFormEnabled && (addressTypeWatch === "airport" || addressTypeWatch === "seaport");
    if (isAirportOrSeaport || countryWatch || state) {
      getTimeZones(true);
    }
  }, [countryWatch, state, addressTypeWatch, isOptionalPortAddressFormEnabled]);

  // When the user selects a service level, set the stop1_after_date to the pickup_ready_date + service_level
  useEffect(() => {
    if (!isServiceLevelEnabled) return;
    // Only set the delivery date if the pickup date is set and the service level is not "NA" or empty
    if (pickupDate && !["NA", ""].includes(serviceLevel)) {
      const stop1AfterDate = moment(pickupDate).add(serviceLevel, "days").format("YYYY-MM-DD");
      setValue("schedule.stop1_after_date", stop1AfterDate);
      setValue("schedule.stop1_after_time", getValues("schedule.pickup_ready_time") || "17:00", { shouldValidate: true });
    }
  }, [serviceLevel, pickupDate]);

  const getTimeZones = (
    countryChange: boolean,
    seaportCountryCode: string = "",
    preferredTimezone: string = "",
  ) => {
    const addressType = getValues(`schedule.${isPickupOrDelivery}_address_type`);
    const isAirportOrSeaport =
      isOptionalPortAddressFormEnabled && (addressType === "airport" || addressType === "seaport");

    // Airport/seaport: fetch full timezone list — do not filter by country/state (matches backend contract).
    if (!isAirportOrSeaport && !countryWatch && !seaportCountryCode) return;

    const countryCode = getValues(`schedule.${isPickupOrDelivery}_country_code`);
    const countryParam = getValues(`schedule.${isPickupOrDelivery}_country`);
    const stateNameParam = getValues(`schedule.${isPickupOrDelivery}_state`);

    const timezoneRequestConfig = isAirportOrSeaport
      ? {}
      : {
          params: {
            country_code: seaportCountryCode || countryCode || countryParam,
            ...(addressType === "address" &&
              stateNameParam && {
                state_name: stateNameParam,
              }),
          },
        };

    getTimezones(timezoneRequestConfig).then((res) => {
      const availableTimezones = res.data.map(({ value, name }: { value: string; name: string }) => ({
        label: name,
        value,
      }));
      if (countryChange && availableTimezones?.length > 0) {
        const timezoneField = `schedule.${isPickupOrDelivery}_timezone`;
        if (!isOptionalPortAddressFormEnabled) {
          setValue(timezoneField, availableTimezones[0].value, { shouldValidate: true });
          setTimezones(availableTimezones);
          return;
        }
        const currentTimezone = preferredTimezone || getValues(timezoneField);
        const hasCurrentTimezone = !!availableTimezones.find((timezone) => timezone.value === currentTimezone);
        const hasSourceSelection =
          addressType === "address"
            ? !!getValues(`schedule.${isPickupOrDelivery}_address_name`)
            : !!(
                getValues(`schedule.${isPickupOrDelivery}_airport_code`) ||
                getValues(`schedule.${isPickupOrDelivery}_airport`) ||
                getValues(`schedule.${isPickupOrDelivery}_seaport_code`) ||
                getValues(`schedule.${isPickupOrDelivery}_seaport`)
              );

        // Never auto-pick a timezone when there is no selected source (address/airport/seaport).
        if (hasCurrentTimezone) {
          setValue(timezoneField, currentTimezone, { shouldValidate: true });
        } else if (hasSourceSelection) {
          setValue(timezoneField, availableTimezones[0].value, { shouldValidate: true });
        }
      }
      setTimezones(availableTimezones);
    });
  };

  return (
    <div>
      <PickupDeliveryAddress
        isPickupOrDelivery={isPickupOrDelivery}
        isQuoteRequest={isQuoteRequest}
        isShipment={isShipment}
        formState={formState}
        isOffPlatformShipment={isOffPlatformShipment}
        contactBook={contactBook}
        countries={countries}
        getTimeZones={getTimeZones}
      />
      <PickupDeliveryContact
        isOffPlatformShipment={isOffPlatformShipment}
        isPickupOrDelivery={isPickupOrDelivery}
        scheduleOptions={scheduleOptions}
        contactBook={contactBook}
        formState={formState}
      />
      <PickupDeliveryPlanning
        isOffPlatformShipment={isOffPlatformShipment}
        isPickupOrDelivery={isPickupOrDelivery}
        scheduleOptions={scheduleOptions}
        isMiniTender={isMiniTender}
        formState={formState}
        isNewShipment={isNewShipment}
        countries={countries}
        timezones={timezones}
      />
    </div>
  );
}

export default React.memo(PickupDelivery);
